import { CardComponentProps } from "../../types/types";
import { SaleTagFilled } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/SaleTagFilled";
import { isDiorBrand } from "@JohnLewisPartnership/jl-design-system/dist/utils/brands/brands";
import classNames from "classnames/bind";
import styles from "./PromoMessages.module.scss";
import { useCallback, useState } from "react";
import { QuickViewModal } from "@~/product-card-components/__shared__/QuickViewModal/QuickViewModal";
import { PromoModal } from "./PromoModal";
import { isOutletProduct } from "@~/utils/product";
import { useWindowSize } from "../../utils/hooks/useWindowSize";
import { dispatchAnalyticsEvent } from "@~/utils/analytics";
import { useProductCardEvent } from "@~/contexts/ProductCardEventContext/ProductCardEventContext.tsx";
import { productCardEvents } from "@~/contexts/ProductCardEventContext/productCardEvents";
import { GLOBAL_INTERACTION } from "@~/utils/events";
import { transformVariantPriceRange } from "../Price/utils";
import { sendMonetateEvent } from "@~/utils/sendMonetateEvent";
import { useFeatureFlags } from "@~/flags/flags";

const cx = classNames.bind(styles);
const SELECTED_PRODUCTS_REDUCED = "Selected Products Reduced";

export function PromoMessages({
  product,
  configProps,
}: Pick<CardComponentProps, "product" | "configProps">) {
  const { reducedToClearTestEnabled } = useFeatureFlags();
  const isReducedToClearTestEnabled = reducedToClearTestEnabled?.value;
  const { width: windowWidth } = useWindowSize();
  const isSmallScreen = windowWidth ? windowWidth < 768 : false;

  const dispatchEvent = useProductCardEvent();

  const [showModal, setShowModal] = useState(false);

  const handleCloseModal = useCallback(function handleCloseModal() {
    setShowModal(false);
  }, []);

  const promoMessages =
    product.messaging
      ?.filter((m) => m.type === "promotional")
      .map((m) => m.title)
      .filter(
        (message) =>
          !isReducedToClearTestEnabled ||
          product.isDepartmentInSale !== false ||
          message.toLowerCase() !== SELECTED_PRODUCTS_REDUCED.toLowerCase(),
      ) || [];

  if (promoMessages.length === 0) {
    return null;
  }

  const isDior = isDiorBrand(product.brand?.name || "");
  const shouldShowModal = !configProps?.hideModal && !isOutletProduct(product);
  const ModalToUse = isSmallScreen ? PromoModal : QuickViewModal;

  const renderOffers = () => {
    return (
      <>
        <button
          type="button"
          className={cx("offersButton", { dior: isDior })}
          aria-label="View offers"
          onClick={() => {
            setShowModal(true);
            sendMonetateEvent("productCard.viewMoreOffers.interaction");
            dispatchEvent(productCardEvents.promoMessagesViewed);
            dispatchAnalyticsEvent(GLOBAL_INTERACTION, {
              type: "plp",
              subType: "promotions modal",
              additionalInfo: {},
              properties: {
                promotions: promoMessages,
              },
              products: [
                {
                  brand: product.brand?.name,
                  sku: product.variants?.[0]?.aliases?.skuId || null,
                  price: product.variantPriceRange
                    ? transformVariantPriceRange(product.variantPriceRange)
                    : undefined,
                  productName: product.title,
                  productId: product.aliases.pimProductId,
                },
              ],
            });
          }}
        >
          <SaleTagFilled aria-hidden="true" />
          <span className={styles.viewOffers} aria-hidden="true">
            View offers
          </span>
        </button>
        <ModalToUse
          product={product}
          showModal={showModal}
          onClose={handleCloseModal}
          originator={"VIEW_OFFERS"}
        ></ModalToUse>
      </>
    );
  };

  const renderPromoMessage = (message: string) => {
    return (
      <p
        className={cx("message", {
          dior: isDior,
        })}
        key={message}
      >
        {message}
      </p>
    );
  };

  return (
    <div data-testid="promo-messages" data-component="pcl-PromoMessages">
      {shouldShowModal && renderPromoMessage(promoMessages[0] || "")}
      {shouldShowModal && promoMessages.length > 1 && renderOffers()}
      {!shouldShowModal &&
        promoMessages.map((message) => renderPromoMessage(message))}
    </div>
  );
}
